import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import type {
  BasicEndorsementDetailsForm,
  BasicEndorsementDetailsState,
  InsuredTabFormValues,
  InsuredTabPolicy,
} from "./types";
import { EMPTY_INSURED_TAB_FORM_VALUES } from "./consts";
import { buildInsuredTabFormValuesFromPolicy } from "./utils";

const initialState: BasicEndorsementDetailsState = {
  effectiveDate: undefined,
  reason: undefined,
  insuredTab: EMPTY_INSURED_TAB_FORM_VALUES,
};

export const basicEndorsementDetailsSlice = createSlice({
  name: "basicEndorsementDetails",
  initialState,
  selectors: {
    /**
     * Default values for the first-step react-hook-form.
     *
     * Memoized so it returns a stable reference until one of the underlying
     * fields changes — avoids re-renders on unrelated endorsement-store updates
     * while keeping an explicit state→form mapping (the transform seam to extend
     * as the slice and form shapes diverge).
     */
    selectBasicEndorsementFormDefaultValues: createSelector(
      [
        (state: BasicEndorsementDetailsState) => state.effectiveDate,
        (state: BasicEndorsementDetailsState) => state.reason,
        (state: BasicEndorsementDetailsState) => state.insuredTab,
      ],
      (
        effectiveDate,
        reason,
        insuredTab
      ): Partial<BasicEndorsementDetailsForm> => ({
        effectiveDate,
        reason,
        insuredTab,
      })
    ),
    selectBasicEndorsementEffectiveDate: (state) => state.effectiveDate,
    /** Whether step 1 has been completed — used to guard later steps. */
    selectBasicEndorsementPopulated: (state) => Boolean(state.effectiveDate),
    selectInsuredTabAdditionalInterests: (state) =>
      state.insuredTab.additionalInterests,
  },
  reducers: {
    setBasicEndorsementDetails(
      state,
      { payload }: PayloadAction<BasicEndorsementDetailsForm>
    ) {
      state.effectiveDate = payload.effectiveDate;
      state.reason = payload.reason;
      state.insuredTab = payload.insuredTab;
    },

    /** Seed the Insured Info step form defaults from a policy. */
    setInsuredTabDetailsFromPolicy(
      state,
      { payload }: PayloadAction<InsuredTabPolicy>
    ) {
      state.insuredTab = buildInsuredTabFormValuesFromPolicy(payload);
    },
    /** Store the submitted Insured Info form values.
        clone values because passing actual values lets Immer freeze them, breaking later form writes. */
    setInsuredTabDetails(
      state,
      { payload }: PayloadAction<InsuredTabFormValues>
    ) {
      state.insuredTab = structuredClone(payload);
    },
    resetBasicEndorsementDetails() {
      return initialState;
    },
  },
});

export const {
  actions: {
    setBasicEndorsementDetails,
    setInsuredTabDetailsFromPolicy,
    setInsuredTabDetails,
    resetBasicEndorsementDetails,
  },
  selectors: {
    selectBasicEndorsementFormDefaultValues,
    selectBasicEndorsementEffectiveDate,
    selectBasicEndorsementPopulated,
    selectInsuredTabAdditionalInterests,
  },
} = basicEndorsementDetailsSlice;

export default basicEndorsementDetailsSlice;
